import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetchData } from "utils";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import type { VariablesOf } from "gql.tada";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RequestItem<Query extends TypedDocumentNode<any, any>> = {
  query: Query;
  variables: VariablesOf<Query>;
};

type RequestQueueState = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queue: RequestItem<TypedDocumentNode<any, any>>[];
  processing: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  enqueue: (item: RequestItem<TypedDocumentNode<any, any>>) => void;
  dequeue: () => void;
  processNext: () => void;
  setOnError: (onError: () => void) => void;
  onError: () => void;
};

export const useRequestQueueStore = create<RequestQueueState>()(
  persist(
    (set, get) => ({
      queue: [],
      processing: false,
      enqueue: (item) => {
        set((state) => ({
          queue: [...state.queue, item],
        }));
        get().processNext();
      },
      dequeue: () => {
        set((state) => ({
          queue: state.queue.slice(1),
          processing: false,
        }));
        get().processNext();
      },
      processNext: () => {
        const { queue, processing, dequeue, onError } = get();
        if (processing || queue.length === 0) {
          return;
        }

        set({ processing: true });

        const { query, variables } = queue[0];

        fetchData({
          query,
          variables,
          onSuccess: () => {
            dequeue();
          },
          onError: () => {
            onError();
            dequeue();
          },
        });
      },
      onError: () => {},
      setOnError: (onError) => set({ onError }),
    }),
    {
      name: "request-queue-storage", // name of the item in the storage (must be unique)
      partialize: (state) => ({
        queue: state.queue,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.queue?.length) {
          state.processNext();
        }
      },
    },
  ),
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const enqueue = (item: RequestItem<TypedDocumentNode<any, any>>) =>
  useRequestQueueStore.getState().enqueue(item);
